'use client';

/**
 * HintLayer — renders the single active proactive hint as a quiet panel in
 * the bottom-left corner. The hint itself is produced by BehaviorEngine;
 * this layer only presents and dismisses it.
 */

import { useEffect } from 'react';
import Link from 'next/link';
import { useWorld } from '../lab/store';
import { useLang } from '../i18n/client';
import { getDict } from '../i18n/dictionary';
import { localePath } from '../i18n/config';
import styles from './HintLayer.module.css';

const AUTO_DISMISS_MS = 30_000;

export default function HintLayer() {
  const lang = useLang();
  const t = getDict(lang).hint;
  const hint = useWorld((s) => s.hint);
  const dismissHint = useWorld((s) => s.dismissHint);
  const reducedMotion = useWorld((s) => s.reducedMotion);

  // A hint that goes unanswered retires itself quietly.
  useEffect(() => {
    if (!hint) return;
    const id = hint.id;
    const t = window.setTimeout(() => dismissHint(id), AUTO_DISMISS_MS);
    return () => window.clearTimeout(t);
  }, [hint, dismissHint]);

  return (
    <div className={styles.region} aria-live="polite">
      {hint && (
        <aside
          className={`panel panel--solid ${styles.hint} ${reducedMotion ? styles.still : ''}`}
          aria-label={t.eyebrow}
        >
          <div className={styles.head}>
            <span className="machine machine--sm">{t.eyebrow}</span>
            <button
              type="button"
              className={styles.close}
              onClick={() => dismissHint(hint.id)}
              aria-label={t.dismiss}
            >
              ✕
            </button>
          </div>
          <p className={styles.text}>{hint.text}</p>
          <Link
            href={localePath(lang, hint.href)}
            className={`machine ${styles.action}`}
            onClick={() => dismissHint(hint.id)}
          >
            {hint.actionLabel}
          </Link>
        </aside>
      )}
    </div>
  );
}
